import { useLinkLogic } from '@/app/navigation/stack-links';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { useCreateTicketStore } from '@/components/editor/TicketPlugin/TicketPlugin';
import { TicketsStoreProvider } from '@/components/editor/TicketPlugin/TicketsStoreProvider';
import { extractMarkdownReferences } from '@/components/editor/extractMarkdownReferences';
import { EntityForm } from '@/components/forms/EntityForm';
import { createTicketDetailLayout } from '@/features/tickets/ticket.schemas';
import {
  usePostTicketEventsMutation,
  useProjectTickets,
} from '@/features/tickets/tickets.hooks';
import { useCrmChainResolver } from '@/features/tickets/useCrmChainResolver';
import { useCurrentUser, useUserMap } from '@/features/users/users.hooks';
import { useEntityForm } from '@/hooks/useEntityForm';
import { markdownReferencesToMeta } from '@/lib/markdownReferencesToMeta';
import { processPlaceholderTickets } from '@/lib/processPlaceholderTickets';
import {
  createTicketEvent,
  CreateTicketPayload,
  EventMeta,
  parseTicketStatus,
  TicketEvent,
} from '@certhon/models';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useRef } from 'react';
import { toast } from 'sonner';

export const TicketCreatePanel = ({
  params,
  path,
  basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const makeLink = useLinkLogic();
  const withCrmChain = useCrmChainResolver();
  const projectId = parseInt(params.projectId);
  const werkId = parseInt(params.werkId);

  const { current: ticket_id } = useRef(crypto.randomUUID());

  const defaultValues = useMemo(() => {
    // Parse URL params for pre-filling new ticket form
    const urlParams = new URLSearchParams(window.location.search);
    const werkIdParam = urlParams.get('werk_id');
    return {
      title: urlParams.get('title') || '',
      description: urlParams.get('description') || '',
      status: parseTicketStatus(urlParams.get('status')) ?? 'opened',
      project_id: parseInt(urlParams.get('project_id')!) || projectId || null,
      werk_id: werkIdParam ? parseInt(werkIdParam) : werkId || null,
      // The creator is the default assignee; the form lets them pick someone else.
      assignee_user_id: user?.recnum ?? null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entityForm = useEntityForm({
    layout: createTicketDetailLayout,
    isLoading: false,
    data: undefined,
    defaultValues,
  });
  const userMap = useUserMap();
  const clientTickets = useProjectTickets(projectId, {
    suspense: false,
  });
  const ticketsStore = useCreateTicketStore(
    clientTickets.data,
    clientTickets.isLoading,
  );

  const postTicketEventsMutation = usePostTicketEventsMutation();

  const handleCancel = () => {
    window.history.back();
    return false;
  };

  return (
    <Panel {...panelProps}>
      <TicketsStoreProvider store={ticketsStore}>
        <EntityForm
          {...entityForm}
          onCancel={handleCancel}
          onSubmit={entityForm.form.handleSubmit(
            async ({ labels, ...formValues }) => {
              try {
                // withCrmChain throws — inside the try so it toasts.
                const payload: CreateTicketPayload = withCrmChain(formValues);
                if (labels?.length) {
                  payload.add_labels = labels;
                }
                // Step 1: Process placeholder tickets in description
                const eventsToPost: TicketEvent[] = [];
                if (payload.description) {
                  const { markdown, ticketEvents } = processPlaceholderTickets(
                    payload.description,
                    {
                      userId: user!.recnum,
                      projectId: payload.project_id,
                      werkId: payload.werk_id,
                      clientId: payload.client_id,
                      originTicketId: ticket_id,
                    },
                  );
                  payload.description = markdown;
                  eventsToPost.push(...ticketEvents);
                }

                // Step 2: Extract references and build meta (including newly created tickets)
                let meta: EventMeta | undefined;
                if (payload.description) {
                  const references = extractMarkdownReferences(
                    payload.description,
                  );
                  if (references.length) {
                    meta = markdownReferencesToMeta(
                      references,
                      userMap,
                      ticketsStore,
                    );
                  }
                }

                // Step 3: Create the main ticket along with placeholder tickets (atomic)
                eventsToPost.push(
                  createTicketEvent('CREATE_TICKET', {
                    user_id: user!.recnum,
                    ticket_id,
                    payload,
                    meta,
                  }),
                );

                await postTicketEventsMutation.mutateAsync(eventsToPost);

                // Navigate back or to the new ticket
                const to = makeLink(path.replace(/new$/, ticket_id));
                navigate({ to });
              } catch (error) {
                toast.error('Could not create ticket', {
                  description:
                    error instanceof Error ? error.message : 'Unknown error', // TODO: Proper error description
                });
              }
            },
            (errors) => {
              console.log('invalid', { errors });
            },
          )}
        />
      </TicketsStoreProvider>
    </Panel>
  );
};
